import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LitPage } from '../lit-page';
import { devWarn, isDifficultyLevel, type DifficultyLevel } from '../luna-common';
import '../luna-icon/luna-icon';

@customElement('luna-preset-row')
export class LunaPresetRow extends LitPage {
  @property({ attribute: 'size-label' }) sizeLabel = '';
  @property({ attribute: 'detail' }) detail = '';

  private _difficulty: DifficultyLevel | null = null;

  @property({ attribute: 'difficulty' })
  get difficulty(): DifficultyLevel | null {
    return this._difficulty;
  }

  set difficulty(value: string | null) {
    const next = String(value ?? '');
    if (next === '') {
      this._difficulty = null;
      return;
    }
    if (!isDifficultyLevel(next)) {
      devWarn(`luna-preset-row: unknown difficulty "${next}" — falling back to "normal"`);
      this._difficulty = 'normal';
      return;
    }
    this._difficulty = next;
  }

  private emitPlay(): void {
    this.dispatchEvent(
      new CustomEvent('luna-play', {
        detail: { sizeLabel: this.sizeLabel, difficulty: this._difficulty },
        bubbles: true,
        composed: true,
      })
    );
  }

  content() {
    return html`
      <div class="luna-preset-row">
        <div class="luna-preset-row__meta">
          <span class="luna-preset-row__size mbw-type-title-topbar">${this.sizeLabel}</span>
          ${this.detail
            ? html`<span class="luna-preset-row__detail">${this.detail}</span>`
            : ''}
          ${this._difficulty
            ? html`<luna-difficulty-pill level=${this._difficulty}></luna-difficulty-pill>`
            : ''}
        </div>
        <button type="button" class="luna-preset-row__play" @click=${this.emitPlay}>
          <luna-icon name="play" size="18" aria-hidden="true"></luna-icon>
          <span class="luna-preset-row__play-label">Play</span>
        </button>
      </div>
    `;
  }

  styles() {
    return css`
      .luna-preset-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        width: 100%;
        height: 84px;
        box-sizing: border-box;
        padding: 14px 18px;
        border-radius: var(--luna-radius-card-sm);
        background: var(--mbw-surface-card);
        box-shadow:
          inset 0 0 0 1px var(--mbw-card-hairline),
          0 3px 0 var(--mbw-card-ledge),
          0 6px 14px rgba(20, 40, 90, 0.35); /* pinned: figma Preset Row (#14285a) */
      }

      .luna-preset-row__meta {
        display: flex;
        align-items: center;
        gap: 16px;
        min-width: 0;
      }

      .luna-preset-row__size {
        color: var(--cds-text-primary);
      }

      .luna-preset-row__detail {
        font-family: var(--mbw-font-sans);
        font-weight: 400;
        font-size: 17px; /* pinned: figma Preset Row (no 17 Regular type style) */
        line-height: 22px;
        color: var(--mbw-text-body);
      }

      .luna-preset-row__play {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        width: 120px;
        height: 56px;
        padding: 0 26px;
        box-sizing: border-box;
        border: none;
        border-radius: var(--luna-radius-btn);
        cursor: pointer;
        color: var(--cds-text-on-color);
        background-image: linear-gradient(180deg, #3f83e6 0%, #0f62fe 25%, #0d5ae8 100%); /* pinned: figma Preset Row */
        box-shadow: inset 0 1px 0 #ffffff, 0 3px 0 var(--luna-edge-blue);
      }

      .luna-preset-row__play:active {
        box-shadow: inset 0 1px 0 #ffffff, 0 1px 0 var(--luna-edge-blue);
      }

      .luna-preset-row__play:focus-visible {
        outline: none;
        box-shadow:
          inset 0 1px 0 #ffffff,
          0 3px 0 var(--luna-edge-blue),
          0 0 0 5px #ffffff,
          0 0 0 7px var(--cds-focus);
      }

      .luna-preset-row__play-label {
        font-family: var(--mbw-font-sans);
        font-weight: 700;
        font-size: 21px; /* pinned: figma Preset Row (drift vs 19px Play/Label) */
        line-height: 27px;
      }
    `;
  }
}
